import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { GridCell } from '../Grid';
import LocationIcon from '../Icons/Location';
import CalendarIcon from '../Icons/Calendar';
import LinkIcon from '../Icons/Link';
import Anchor from '../Anchor';
import Body2 from '../Typography/Body/Body2';
import Body1 from '../Typography/Body/Body1';
import Headline3 from '../Typography/Heading/Headline3';
import breakpoints from '../Theme/breakpoints';

const StyledGridCell = styled(GridCell)`
  &&& {
    max-width: 40rem;
    margin: 0 auto;
  }
`;

const SubContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;

  svg {
    margin: 4px 12px 4px 0;
  }
`;

const CenterText = styled.div`
  text-align: center;
`;

const Container = styled.div`
  padding: 24px;

  @media only screen and (min-width: ${breakpoints.sm}px) {
    padding: 0;
  }
`;

const TitleContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 168px);
  word-break: break-word;
  justify-content: center;
  margin: -24px 0 24px;

  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
    margin: 0;
    padding: 0;
  }
`;

const StyledDiv = styled.div`
  margin-top: 24px;
`;

const StyledBody2 = styled(Body2)`
  && {
    margin: 0;
  }
`;

const StyledHeadline3 = styled(Headline3)`
  && {
    margin: 24px 0;
  }
`;

const StyledBody1 = styled(Body1)`
  && {
    margin-bottom: 32px;
    word-break: break-word;

    p {
      font-family: ${({ theme }) => theme.fonts.primary};
    }
  }
`;

const eventPropertyTypes = {
  image: PropTypes.node,
  breadcrumbs: PropTypes.node,
  title: PropTypes.string,
  content: PropTypes.node,
  location: PropTypes.string,
  formattedStartDate: PropTypes.node,
  formattedEndDate: PropTypes.node,
  website: PropTypes.string,
  backButton: PropTypes.node,
};

type EventProperties = InferProps<typeof eventPropertyTypes> & {
  title?: string;
};

const Event = ({
  image,
  breadcrumbs,
  title,
  content,
  location,
  formattedStartDate,
  formattedEndDate,
  website,
  backButton,
}: EventProperties) => (
  <StyledGridCell padding="0 20px 20px">
    {breadcrumbs}
    <StyledDiv>
      <TitleContainer>
        <StyledHeadline3 tag="h1">
          {title}
        </StyledHeadline3>
      </TitleContainer>
      {image}
      <Container>
        <StyledBody1 tag="div">
          {content}
        </StyledBody1>
        <SubContainer>
          <LocationIcon title={title} />
          <StyledBody2>
            {' '}
            {location}
          </StyledBody2>
        </SubContainer>
        <SubContainer>
          <CalendarIcon title={title} />
          <StyledBody2>
            {formattedStartDate}
            {' '}
            -
            {' '}
            {formattedEndDate}
          </StyledBody2>
        </SubContainer>
        {website
          && (
            <SubContainer>
              <LinkIcon title={title} />
              <Anchor
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                styled
              >
                <StyledBody2>
                  {title}
                </StyledBody2>
              </Anchor>
            </SubContainer>
          )}
      </Container>
      <CenterText>
        {backButton}
      </CenterText>
    </StyledDiv>
  </StyledGridCell>
);

Event.propTypes = eventPropertyTypes;

Event.defaultProps = {
  image: undefined,
  breadcrumbs: undefined,
  title: undefined,
  content: undefined,
  location: undefined,
  formattedStartDate: undefined,
  formattedEndDate: undefined,
  website: undefined,
  backButton: undefined,
};

export default Event;
